import { ComponentRef, Injectable, NgZone } from "@angular/core";
import { Subject, fromEvent, merge } from "rxjs";
import { throttleTime } from "rxjs/operators";
import { AppComponent } from "src/app/app.component";

@Injectable({
  providedIn: "root",
})
export class StartNgIdleService {
  modalComponent: ComponentRef<AppComponent>;
  public timeOut = new Subject<boolean>();
  private isInitialized = false;
  private lastActivityTime: number = Date.now();
  private idleCheckInterval: any;
  private readonly IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes until warning
  private readonly LOGOUT_AFTER_WARNING_MS = 5 * 60 * 1000; // 5 minute countdown
  private isShowingWarning = false;
  private warningStartTime: number = 0;

  constructor(private ngZone: NgZone) {}

  setIdleCountdown(): void {
    // Don't start idle tracking if the user was already timed out
    if (sessionStorage.getItem("timeOut") === "true") {
      return;
    }

    if (!this.isInitialized) {
      this.isInitialized = true;
      this.lastActivityTime = Date.now();

      this.setupActivityTracking();

      // Check idle status every 10 seconds
      this.ngZone.runOutsideAngular(() => {
        this.idleCheckInterval = setInterval(() => {
          this.checkIdleStatus();
        }, 10000);
      });
    }
  }

  private setupActivityTracking(): void {
    const mouseMove$ = fromEvent(document, "mousemove");
    const mouseDown$ = fromEvent(document, "mousedown");
    const keyDown$ = fromEvent(document, "keydown");
    const scroll$ = fromEvent(document, "scroll");
    const touchStart$ = fromEvent(document, "touchstart");
    const click$ = fromEvent(document, "click");

    merge(mouseMove$, mouseDown$, keyDown$, scroll$, touchStart$, click$)
      .pipe(throttleTime(500))
      .subscribe(() => {
        // Don't reset activity timer while the warning dialog is showing;
        // only the explicit "Continue" button (via resetIdleTimer) should dismiss it.
        if (!this.isShowingWarning) {
          this.lastActivityTime = Date.now();
        }
      });
  }

  private checkIdleStatus(): void {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    // If warning is showing, check if 5 minutes have passed since warning started
    if (this.isShowingWarning) {
      const timeSinceWarning = now - this.warningStartTime;
      if (timeSinceWarning >= this.LOGOUT_AFTER_WARNING_MS) {
        sessionStorage.setItem("timeOut", "true");
        // Timeout component handles the actual logout
      }
      return;
    }

    // Check if user has been idle for 20 minutes
    if (timeSinceLastActivity >= this.IDLE_TIMEOUT_MS) {
      this.isShowingWarning = true;
      this.warningStartTime = now;
      this.ngZone.run(() => {
        this.timeOut.next(true);
      });
    }
  }

  getTimeout() {
    return this.timeOut;
  }

  resetIdleTimer(): void {
    this.lastActivityTime = Date.now();
    this.isShowingWarning = false;
    this.warningStartTime = 0;
    this.ngZone.run(() => {
      this.timeOut.next(false);
    });
  }
}
